import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { BitmapText, Color, Container, FillGradient, Sprite, Text, TextStyle } from "pixi.js";
import { Easing, Group, Tween } from "tweedle.js";
import { currentBackgorundMusic, currentBackgorundMusicVolume, isMusicBtnOnGlobal, languageGlobal, setBackgorundMusic } from "../config/GlobalVariableConfig";
import { getFontConfig } from "../config/introTextConfig";
import { GAME_LAYOUT_HEIGHT, GAME_LAYOUT_WIDTH } from "../config/reelConfig";
import { isFireFox } from "../utils";

declare const window: any;
const translationManager = window.hacksawCasino.Translation;

// import { setBackgorundMusic, currentBackgorundMusicVolume } from "../config/GlobalVariableConfig";

export class Banner extends Container {

    private bgContainer = new Container();
    private texContainer = new Container(); //-- which needed to scale without any limit
    private charContainer = new Container();

    // Dedicated Containers for Intro and Outro text elements
    private introContainer = new Container();
    private outroContainer = new Container();

    private backgroundImg: Sprite;
    // private backgroundMidImg: Sprite;

    // --- INTRO TEXTS ---
    private introHeading: Text = new Text({});
    private introDesc1: Text = new Text({});
    private introDesc2: Text = new Text({});
    // private bannerText4: Text = new Text({});
    // private bannerText5: Text = new Text({});
    private introContinue: Text = new Text({});

    // --- OUTRO TEXTS ---
    private outroHeading: Text = new Text({});
    private outroDesc1: Text = new Text({});
    private outroContinue: Text = new Text({});
    private winText: BitmapText = new BitmapText({
        text: "",
        style: {
            fontFamily: "dtt_coin_font",
            trim: true,
            fontSize: 150,
            letterSpacing: 5,
        }
    });

    // private backgroundSpine: Spine | null = null;

    private leftCharacter: Spine | null = null;
    private rightCharacter: Spine | null = null;
    private intro_outro_banner: Spine | null = null;

    private tweenGroup: Group = new Group();

    private screenWidth: number;
    private screenHeight: number;


    constructor(screenWidth: number, screenHeight: number) {
        super();

        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        this.setupBGImage();
        this.setupBackground();

        // Separated text setups
        this.setupIntroBannerText();
        this.setupOutroBannerText();

        this.setupCharacter();

        this.visible = false;

        // this.bgContainer.eventMode = "static"
        this.resize(screenWidth, screenHeight);
    }

    private setupBGImage(): void {

        this.backgroundImg = Sprite.from("freegame_intro_bg");
        this.backgroundImg.label = "backgroundImg";
        this.backgroundImg.alpha = 0;
        this.addChild(this.backgroundImg);
    }

    private setupBackground(): void {
        // this.backgroundSpine = Spine.from({
        //     skeleton: "bannerSkel",
        //     atlas: "bannerAtlas",
        // });

        // this.backgroundSpine.position.set(1920 / 2, 1080 / 2);
        // spineSymbol.scale=0.1;
        // this.backgroundSpine.state.setAnimation(0, "loop", true);

        // this.bgContainer.addChild(this.backgroundSpine);

        this.addChild(this.bgContainer);
        this.addChild(this.texContainer);
        this.addChild(this.charContainer);

        // Add modular state containers to the main scaling text container
        this.texContainer.addChild(this.introContainer);
        this.texContainer.addChild(this.outroContainer);
    }

    private setupCharacter(): void {
        /**
         * Z-ORDERING EXPLANATION:
         * 1. Drill (bgContainer) - Backmost
         * 2. Banner Frame (bgContainer) - Behind Characters, in front of Drill
         * 3. Characters (charContainer) - Frontmost
         */

        // 1. Setup Drill (Backmost)
        this.leftCharacter = Spine.from({
            skeleton: "bannerDrillSkel",
            atlas: "bannerDrillAtlas"
        });
        this.leftCharacter.position.set(100, 400);
        // this.bgContainer.addChild(this.leftCharacter);

        // 2. Setup Banner Frame (Middle layer)
        this.intro_outro_banner = Spine.from({
            skeleton: "bannerFrameSkel",
            atlas: "bannerFrameAtlas"
        });
        this.intro_outro_banner.label = "intro_outro_banner";
        this.intro_outro_banner.position.set(950, 200);
        this.bgContainer.addChild(this.intro_outro_banner);

        // 3. Setup Miner/Character (Frontmost layer)
        this.rightCharacter = Spine.from({
            skeleton: "bannerCharSkel",
            atlas: "bannerCharAtlas"
        });
        this.rightCharacter.position.set(1800, 200);
        this.bgContainer.addChild(this.rightCharacter);
    }

    // Modularized Intro Text Setup
    private setupIntroBannerText(): void {
        const { fontFamily: fontFamilyHeading, fontWeight: fontWeightHeading, fontSize: fontSizeHeading } = getFontConfig(languageGlobal, "introHeading");

        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);
        const colors = ["#f5b504", "#fffff2", "#f7c714"].map((color) => Color.shared.setValue(color).toNumber());
        colors.forEach((number, index) => fill.addColorStop(index / colors.length, number));

        this.introHeading.style = new TextStyle({
            fontFamily: fontFamilyHeading, fill: { fill }, fontWeight: fontWeightHeading, fontSize: fontSizeHeading,
            stroke: { color: "#744303", width: 3 },
            dropShadow: isFireFox() ? false : { blur: 5, color: "#000000", distance: 6 },
            letterSpacing: 1, align: "center",
        });

        const { fontFamily: fontFamilyDesc1, fontWeight: fontWeightDesc1, fontSize: fontSizeDesc1, lineHeight: lineHeightDesc1 } = getFontConfig(languageGlobal, "introDesc1");
        this.introDesc1.style = new TextStyle({
            fontFamily: fontFamilyDesc1, fill: { fill }, fontWeight: fontWeightDesc1, fontSize: fontSizeDesc1,
            stroke: { color: "#744303", width: 3 },
            dropShadow: isFireFox() ? false : { blur: 5, color: "#000000", distance: 6 },
            align: "center", letterSpacing: 1, breakWords: true, wordWrap: true, wordWrapWidth: 1050, lineHeight: lineHeightDesc1,
        });

        const { fontFamily: fontFamilyDesc2, fontWeight: fontWeightDesc2, fontSize: fontSizeDesc2, lineHeight: lineHeightDesc2 } = getFontConfig(languageGlobal, "introDesc2");
        const desc2TextStyle = new TextStyle({
            fontFamily: fontFamilyDesc2, fill: "#ffffff", fontWeight: fontWeightDesc2, fontSize: fontSizeDesc2,
            align: "center", wordWrap: true, letterSpacing: 1, wordWrapWidth: 1000, lineHeight: lineHeightDesc2, breakWords: true,
        });

        this.introDesc2.style = desc2TextStyle;
        if (languageGlobal === "zh-tw") this.introDesc2.style.fontSize = 42;
        // this.bannerText4.style = desc2TextStyle;
        // this.bannerText5.style = desc2TextStyle;

        const { fontFamily: fontFamilyContinue, fontWeight: fontWeightContinue, fontSize: fontSizeContinue2 } = getFontConfig(languageGlobal, "introContinue");
        this.introContinue.style = new TextStyle({
            fontFamily: fontFamilyContinue, fill: { fill }, fontWeight: fontWeightContinue, fontSize: fontSizeContinue2,
            stroke: { color: "#744303", width: 3 },
            dropShadow: isFireFox() ? false : { blur: 5, color: "#000000", distance: 6 },
        });

        this.introContinue.text = translationManager.game('intro_continue_uc');

        this.introHeading.anchor.set(0.5);
        this.introDesc1.anchor.set(0.5);
        this.introDesc2.anchor.set(0.5);
        // this.bannerText4.anchor.set(0.5);
        // this.bannerText5.anchor.set(0.5);
        this.introContinue.anchor.set(0.5);

        this.introContainer.addChild(this.introHeading, this.introDesc1, this.introDesc2, this.introContinue);
        // this.introContainer.addChild(this.bannerText4, this.bannerText5);
    }

    // Modularized Outro Text Setup
    private setupOutroBannerText(): void {
        // Reusing styles from intro for consistency, but separating objects
        this.outroHeading.style = this.introHeading.style.clone();
        this.outroDesc1.style = this.introDesc1.style.clone();
        this.outroContinue.style = this.introContinue.style.clone();
        this.outroContinue.text = translationManager.game('intro_continue_uc');

        this.outroHeading.anchor.set(0.5);
        this.outroDesc1.anchor.set(0.5);
        this.winText.anchor.set(0.5);
        this.outroContinue.anchor.set(0.5);

        this.outroContainer.addChild(this.outroHeading, this.outroDesc1, this.winText, this.outroContinue);
    }

    // Helper function to manage Spine animations cleanly
    private playSpineAnimation(spine: Spine | null, animationName: string, loop: boolean = false, nextAnim?: string): Promise<void> {
        if (!spine) return Promise.resolve();
        return new Promise<void>((resolve) => {
            spine.state.setAnimation(0, animationName, loop).listener = {
                complete: () => {
                    spine.state.clearListeners();
                    if (nextAnim) {
                        spine.state.setAnimation(0, nextAnim, true);
                    }
                    resolve();
                }
            };
        });
    }

    public async appearFreeGameIntroBanner(freeGameNumber: number) {
        return new Promise<void>(async (resolve) => {
            // 0. Prepare state
            this.visible = true;
            this.introContainer.visible = true;
            this.outroContainer.visible = false;
            this.intro_outro_banner.visible = false; // Hide frame initially
            this.texContainer.alpha = 0;
            this.texContainer.scale.set(0);

            // Configure Texts
            this.introHeading.text = translationManager.game('banner_intro_heading_uc');
            this.introHeading.scale = Math.min(1, 800 / this.introHeading.width);
            // this.introHeading.position.set(0, -270);
            this.introDesc1.text = translationManager.game('banner_intro_desc_1', null, { 'freegamenumber': freeGameNumber });
            this.introDesc1.scale = Math.min(1, 820 / this.introDesc1.width);
            // this.introDesc1.position.set(0, -170);
            this.introDesc2.text = translationManager.game('banner_intro_desc_2');
            // this.introDesc2.position.set(0, 50);
            this.introContinue.position.set(22, 275);

            // 1. Show the background
            await new Promise<void>((resolveTween) => {

                const tween = new Tween(this.backgroundImg, this.tweenGroup)
                    .to({ alpha: 1 }, 400)
                    .onComplete(() => {
                        this.tweenGroup.remove(tween);
                        resolveTween();
                    })
                    .start();
            });

            // 2. Pull up the Drill and Character animation
            sound.play('free_Game_Banner', { volume: 1 });
            this.playSpineAnimation(this.leftCharacter, "in", false, "loop"); // Drill in
            this.playSpineAnimation(this.rightCharacter, "in", false, "loop"); // Miner in

            // Start vertical jiggle to look like it is drilling
            // const jiggle = new Tween(this.leftCharacter.position, this.tweenGroup)
            //     .to({ y: this.leftCharacter.position.y + 5 }, 50)
            //     .yoyo(true)
            //     .repeat(20) // Play for ~1 second
            //     .start();

            this.applyScreenShake(10, 1000); // Screen shake during drilling

            // 3. Bring the Banner Frame (after drilling a bit)
            await new Promise(r => setTimeout(r, 800));
            this.intro_outro_banner.visible = true;
            await this.playSpineAnimation(this.intro_outro_banner, "in", false, "loop");

            // 4 & 5. Slam the texts on the frame with a little screen shake
            await this.appearText(true, this.introContinue);
            this.applyScreenShake(15, 300); // Slam shake

            resolve();
        });
    }

    public async appearFreeGameOutroBanner(totalWin: number, freeSpins: number) {
        return new Promise<void>(async (resolve) => {
            // State management
            this.visible = true;
            this.introContainer.visible = false;
            this.outroContainer.visible = true;

            // Configure Outro specific texts
            this.outroHeading.text = translationManager.game('banner_outro_heading_uc');
            this.outroHeading.scale = Math.min(1, 900 / this.outroHeading.width);
            this.outroHeading.position.set(0, -200);

            this.outroDesc1.text = translationManager.game('banner_outro_desc_1', null, { 'winnumber': freeSpins });
            this.outroDesc1.scale = Math.min(1, 750 / this.outroDesc1.width);
            this.outroDesc1.position.set(0, -120);

            this.winText.text = totalWin.toFixed(2);
            this.winText.scale = Math.min(1, 900 / this.winText.width);
            this.winText.position.set(20, 40);

            this.outroContinue.position.set(20, 250);

            this.texContainer.alpha = 0;
            this.texContainer.scale = 0;

            await new Promise<void>((resolveTween) => {
                const obj = { alpha: 0 };
                const tween = new Tween(obj, this.tweenGroup)
                    .to({ alpha: 1 }, 200)
                    .onUpdate(() => {
                        this.backgroundImg.alpha = obj.alpha;
                        // this.backgroundMidImg.alpha = obj.alpha;
                    })
                    .onStart(() => sound.play('total_win', { volume: 0.5 }))
                    .onComplete(() => {
                        this.tweenGroup.remove(tween);
                        resolveTween();
                    })
                    .start();
            });

            // Cleanly play all spines in parallel
            const promises = [
                this.playSpineAnimation(this.intro_outro_banner, "in", false, "loop"),
                this.playSpineAnimation(this.leftCharacter, "in", false, "loop"),
                this.playSpineAnimation(this.rightCharacter, "in", false, "loop")
            ];

            await Promise.all(promises);
            this.applyScreenShake(8, 200); // Smaller shake for outro win
            await this.appearText(false, this.outroContinue);
            resolve();
        });
    }

    private applyScreenShake(intensity: number, duration: number) {
        const originalPos = { x: this.x, y: this.y };
        const shakeObj = { t: 0 };

        new Tween(shakeObj, this.tweenGroup)
            .to({ t: 1 }, duration)
            .onUpdate(() => {
                const currentIntensity = intensity * (1 - shakeObj.t); // Intensity fades out
                this.x = originalPos.x + (Math.random() - 0.5) * currentIntensity;
                this.y = originalPos.y + (Math.random() - 0.5) * currentIntensity;
            })
            .onComplete(() => {
                this.x = originalPos.x;
                this.y = originalPos.y;
            })
            .start();
    }

    // Accepts the specific continue text we want to animate based on state
    private appearText(toFreeGame: boolean, continueTextNode: Text) {
        return new Promise<void>((resolve) => {
            let textScale = (this.screenWidth > this.screenHeight)
                ? Math.min(this.screenWidth / 1920, this.screenHeight / 1080)
                : Math.min(this.screenWidth / 1080, this.screenHeight / 1920);

            this.texContainer.alpha = 0;
            // ENHANCEMENT: Start text slightly larger for a "pop-in" effect
            this.texContainer.scale.set(textScale * 1.3);

            const tweenAlpha = new Tween(this.texContainer, this.tweenGroup)
                .to({ scale: { x: textScale, y: textScale }, alpha: 1 }, 500)
                .easing(Easing.Back.Out) // ENHANCEMENT: Adds a bouncy "overshoot" effect
                .onStart(() => this.switchTheBackgroundMusic(toFreeGame))
                .onComplete(() => {
                    this.eventMode = "static";
                    // this.bgContainer.cursor = 'pointer';
                    this.tweenGroup.remove(tweenAlpha);
                    resolve();
                })
                .start();

            // Animate the correct continue text based on state
            // Reset scale first to prevent stacking tweens
            continueTextNode.scale.set(1);
            const tweenBreath = new Tween(continueTextNode.scale, this.tweenGroup)
                .to({ x: 1.05, y: 1.05 }, 1000)
                .yoyo(true)
                .repeat(Infinity)
                .onComplete(() => this.tweenGroup.remove(tweenBreath))
                .start();
        });
    }

    public async disappearBanner(): Promise<void> {
        return new Promise<void>(async (resolve) => {
            // if (!isBase) {
            //     this.switchTheBackgroundMusic(false);
            // }

            this.texContainer.alpha = 0;
            this.texContainer.scale = 0;

            // Utilize the helper method to cleanly trigger the "out" animations
            const promises = [
                this.playSpineAnimation(this.intro_outro_banner, "out", false),
                this.playSpineAnimation(this.leftCharacter, "out", false),
                this.playSpineAnimation(this.rightCharacter, "out", false)
            ];

            const obj = { alpha: 1 };
            await new Promise<void>((resolveTween) => {
                const tween = new Tween(obj, this.tweenGroup)
                    .to({ alpha: 1 }, 200)
                    .onUpdate(() => {
                        this.backgroundImg.alpha = obj.alpha;
                        // this.backgroundMidImg.alpha = obj.alpha;
                    })
                    .onComplete(() => {
                        this.tweenGroup.remove(tween);
                        resolveTween();
                    })
                    .start();
            });

            await Promise.all(promises);
            this.visible = false;
            this.eventMode = "none";
            // this.bgContainer.cursor = 'none';
            resolve();
        });
    }

    private switchTheBackgroundMusic(toFreeGame: boolean) {
        let targetMusicLoop = toFreeGame ? sound.find('free_background_loop') : sound.find('base_music_loop');

        let currentVal: number = currentBackgorundMusicVolume;
        let targetVal: number = 0;

        const tween = new Tween({ x: currentVal }, this.tweenGroup)
            .to({ x: targetVal }, 750)
            .onUpdate((object) => {
                currentBackgorundMusic.volume = object.x;
            })
            .onComplete(() => {
                this.tweenGroup.remove(tween);
                playFgMusic();
            })
            .start();

        const playFgMusic = () => {
            let currentVal: number = .10;
            let targetVal: number = currentBackgorundMusicVolume;

            setBackgorundMusic(targetMusicLoop);

            const tweenFg = new Tween({ x: currentVal }, this.tweenGroup)
                .to({ x: targetVal }, 750)
                .onStart(() => {
                    currentBackgorundMusic.loop = true;
                    currentBackgorundMusic.singleInstance = true;
                    currentBackgorundMusic.play();
                    currentBackgorundMusic.muted = !isMusicBtnOnGlobal;
                })
                .onUpdate((object) => {
                    currentBackgorundMusic.volume = object.x;
                })
                .onComplete(() => {
                    this.tweenGroup.remove(tweenFg);
                })
                .start();
        }
    }

    public resize(screenWidth: number, screenHeight: number): void {
        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        let scale: number;

        let bgScale;
        const BG_SCALE_FACTOR = Math.max(screenWidth / GAME_LAYOUT_WIDTH, screenHeight / GAME_LAYOUT_HEIGHT);

        if (screenWidth > screenHeight) {
            scale = Math.min(screenWidth / 1920, screenHeight / 1080);
            bgScale = Math.max(scale, BG_SCALE_FACTOR);

            this.texContainer.scale.set(scale, scale);

            const xPos = (GAME_LAYOUT_WIDTH * bgScale - screenWidth) / 2;
            const yPos = (GAME_LAYOUT_HEIGHT * bgScale - screenHeight) / 2;
            this.backgroundImg.scale = bgScale;
            this.backgroundImg.x = -xPos;
            this.backgroundImg.y = -yPos;

            this.introHeading.position.set(0, -270);
            this.introDesc1.position.set(0, -170);
            this.introDesc2.position.set(0, 50);
            this.introContinue.position.set(22, 275);

            this.leftCharacter.scale = 0.6;
            this.leftCharacter.position.set(700, 850);
            this.rightCharacter.scale = 0.8;
            this.rightCharacter.position.set(1100, 800);
            this.intro_outro_banner.position.set(950, 600);
            this.intro_outro_banner.scale = 1; // Reset scale if manipulated elsewhere

            // this.backgroundMidImg.scale = 1;
            // this.backgroundMidImg.position.set(355, -90);

            this.bgContainer.scale.set(scale, scale)
            const bgXPos = (1920 * scale - screenWidth) / 2;
            const bgYPos = (1080 * scale - screenHeight) / 2;
            this.bgContainer.position.set(-bgXPos, -bgYPos);
            this.charContainer.scale.set(scale, scale)
            this.charContainer.position.set(-bgXPos, -bgYPos);

            this.texContainer.position.set(screenWidth / 2, screenHeight / 2);
        }
        else {
            scale = Math.min(screenWidth / 1080, screenHeight / 1920);
            bgScale = Math.max(scale, BG_SCALE_FACTOR);

            this.texContainer.scale.set(scale, scale);

            const xPos = (GAME_LAYOUT_WIDTH * bgScale - screenWidth) / 2;
            const yPos = (GAME_LAYOUT_HEIGHT * bgScale - screenHeight) / 2;
            this.backgroundImg.scale = bgScale;
            this.backgroundImg.x = -xPos;
            this.backgroundImg.y = -yPos;

            this.leftCharacter.scale = 0.6;
            this.leftCharacter.position.set(600, -175);
            this.rightCharacter.scale = 0.75;
            this.rightCharacter.position.set(725, 1180);

            this.introHeading.position.set(20, -200);
            this.introDesc1.position.set(20, -130);
            this.introDesc2.position.set(20, 65);
            this.introContinue.position.set(22, 275);

            this.intro_outro_banner.position.set(960, 600);
            this.intro_outro_banner.scale = 0.975;

            // this.backgroundMidImg.scale = 1;
            // this.backgroundMidImg.position.set(340, -90);

            this.bgContainer.scale.set(scale, scale)
            const bgXPos = (1920 * scale - screenWidth) / 2;
            const bgYPos = (1080 * scale - screenHeight) / 2;
            this.bgContainer.position.set(-bgXPos, -bgYPos);
            this.charContainer.scale.set(scale, scale)
            this.charContainer.position.set(-bgXPos - 200, -bgYPos);

            this.texContainer.position.set((screenWidth / 2) - 16 * scale, screenHeight / 2);
        }
    }

    public update(): void {
        this.tweenGroup.update();
    }
}